import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Grid,
  GridItem,
  useColorModeValue,
  Heading,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Divider,
  Avatar,
  SimpleGrid,
  Icon,
} from "@chakra-ui/react";
import Card from "../components/Card/Card";
import CardBody from "../components/Card/CardBody";
import CardHeader from "../components/Card/CardHeader";
import { useQuery } from 'react-query';
import { getAppointments } from '../services/appointmentService';
import { getClients } from '../services/clientService';
import { useAuth } from '../contexts/AuthContext';
import JalaliDateDisplay from '../components/JalaliDateDisplay';
import { 
  FaCalendarAlt, 
  FaClock, 
  FaUsers, 
  FaUserTie, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaHourglassHalf, 
  FaMoneyBillWave, 
  FaUser,
  FaChartLine,
  FaStar,
  FaCalendarCheck,
  FaCalendarTimes,
  FaUserClock
} from 'react-icons/fa';

function ConsultantDashboard() {
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const { user } = useAuth();
  
  // دریافت تاریخ امروز
  const today = new Date();
  const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD format

  // دریافت ملاقات‌های مشاور جاری
  const {
    data: appointmentsData,
    isLoading: isLoadingAppointments,
    error: appointmentsError
  } = useQuery(
    ['consultant-appointments', user?.id],
    () => getAppointments({ consultantId: user?.id, limit: 1000 }),
    {
      staleTime: 5 * 60 * 1000, // 5 دقیقه
      enabled: !!user?.id,
    }
  );

  // دریافت مراجعان مشاور جاری
  const {
    data: clientsData,
    isLoading: isLoadingClients
  } = useQuery(
    ['consultant-clients', user?.id],
    () => getClients({ consultantId: user?.id, limit: 1000 }),
    {
      staleTime: 10 * 60 * 1000, // 10 دقیقه
      enabled: !!user?.id,
    }
  );

  // محاسبه آمار
  const calculateStats = () => {
    if (!appointmentsData || !user?.id) return {};

    const appointments = appointmentsData.data?.appointments || appointmentsData.appointments || appointmentsData.data || appointmentsData || [];
    const clients = clientsData?.data?.clients || clientsData?.clients || clientsData?.data || clientsData || [];

    // ملاقات‌های امروز
    const todayAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date).toISOString().split('T')[0];
      return aptDate === todayString;
    });

    // ملاقات‌های امروز که هنوز ساعتشان فرا نرسیده
    const upcomingTodayAppointments = todayAppointments.filter(apt => {
      const aptTime = apt.time;
      const currentTime = new Date().toTimeString().slice(0, 5); // HH:MM format
      return aptTime > currentTime;
    });

    // ملاقات‌های هفته جاری
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    const thisWeekAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate >= startOfWeek && aptDate <= endOfWeek;
    });

    // ملاقات‌های ماه جاری
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    const thisMonthAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate >= startOfMonth && aptDate <= endOfMonth;
    });

    // آمار وضعیت‌های ملاقات
    const appointmentStatusStats = appointments.reduce((acc, apt) => {
      const status = apt.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // آمار وضعیت‌های پرداخت
    const paymentStatusStats = appointments.reduce((acc, apt) => {
      const paymentStatus = apt.paymentStatus || 'unknown';
      acc[paymentStatus] = (acc[paymentStatus] || 0) + 1;
      return acc;
    }, {});

    // آمار ملاقات‌های امروز بر اساس وضعیت
    const todayStatusStats = todayAppointments.reduce((acc, apt) => {
      const status = apt.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // آمار ملاقات‌های امروز بر اساس وضعیت پرداخت
    const todayPaymentStats = todayAppointments.reduce((acc, apt) => {
      const paymentStatus = apt.paymentStatus || 'unknown';
      acc[paymentStatus] = (acc[paymentStatus] || 0) + 1;
      return acc;
    }, {});

    // محاسبه درآمد
    const totalEarnings = appointments
      .filter(apt => apt.paymentStatus === 'paid')
      .reduce((sum, apt) => sum + (apt.cost || 0), 0);

    // محاسبه درآمد امروز
    const todayEarnings = todayAppointments
      .filter(apt => apt.paymentStatus === 'paid')
      .reduce((sum, apt) => sum + (apt.cost || 0), 0);

    // محاسبه درآمد هفته
    const thisWeekEarnings = thisWeekAppointments
      .filter(apt => apt.paymentStatus === 'paid')
      .reduce((sum, apt) => sum + (apt.cost || 0), 0);

    // محاسبه درآمد ماه
    const thisMonthEarnings = thisMonthAppointments
      .filter(apt => apt.paymentStatus === 'paid')
      .reduce((sum, apt) => sum + (apt.cost || 0), 0);

    // محاسبه نرخ تکمیل ملاقات‌ها
    const completedAppointments = appointments.filter(apt => apt.status === 'completed').length;
    const completionRate = appointments.length > 0 ? (completedAppointments / appointments.length) * 100 : 0;

    // محاسبه نرخ حضور مراجعان
    const noShowAppointments = appointments.filter(apt => apt.status === 'no-show').length;
    const attendanceRate = appointments.length > 0 ? ((appointments.length - noShowAppointments) / appointments.length) * 100 : 0;

    return {
      totalAppointments: appointments.length,
      todayAppointments: todayAppointments.length,
      thisWeekAppointments: thisWeekAppointments.length,
      thisMonthAppointments: thisMonthAppointments.length,
      upcomingTodayAppointments: upcomingTodayAppointments.length,
      totalClients: clients.length,
      totalEarnings,
      todayEarnings,
      thisWeekEarnings,
      thisMonthEarnings,
      completionRate,
      attendanceRate,
      appointmentStatusStats,
      paymentStatusStats,
      todayStatusStats,
      todayPaymentStats,
      upcomingTodayAppointments,
      thisWeekAppointments,
      thisMonthAppointments
    };
  };

  const stats = calculateStats();

  // تبدیل وضعیت به فارسی
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'در انتظار تأیید';
      case 'confirmed': return 'تأیید شده';
      case 'completed': return 'تکمیل شده';
      case 'cancelled': return 'لغو شده';
      case 'no-show': return 'عدم حضور';
      default: return status;
    }
  };

  // تبدیل وضعیت پرداخت به فارسی
  const getPaymentStatusText = (status) => {
    switch (status) {
      case 'pending': return 'در انتظار پرداخت';
      case 'paid': return 'پرداخت شده';
      case 'partial': return 'پرداخت جزئی';
      case 'refunded': return 'بازگشت وجه';
      default: return status;
    }
  };

  // رنگ‌های وضعیت
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'confirmed': return 'blue';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      case 'no-show': return 'orange';
      default: return 'gray';
    }
  };

  // رنگ‌های وضعیت پرداخت
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'paid': return 'green';
      case 'partial': return 'blue';
      case 'refunded': return 'red';
      default: return 'gray';
    }
  };

  if (isLoadingAppointments || isLoadingClients) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="teal.500" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری داشبورد...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  if (appointmentsError) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle>خطا در بارگذاری داشبورد!</AlertTitle>
              <AlertDescription>
                {appointmentsError.message || 'خطا در ارتباط با سرور'}
              </AlertDescription>
            </Alert>
          </Box>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
      <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
        <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
          {/* Header با اطلاعات مشاور */}
          <Box p="20px 0px 35px 0px">
            <Flex direction="column">
              <HStack spacing={4} mb={4}>
                <Avatar size="lg" name={user?.name || 'مشاور'} bg="teal.500" />
                <Box>
                  <Text fontSize="lg" color={textColor} fontWeight="bold" me="auto">
                    داشبورد مشاور
                  </Text>
                  <Text fontSize="md" fontWeight="400" me="auto" color="gray.400">
                    {user?.name || 'مشاور'} - خلاصه‌ای از فعالیت‌های شما
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    <JalaliDateDisplay date={today} />
                  </Text>
                </Box>
              </HStack>
            </Flex>
          </Box>

          {/* آمار کلی */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={8}>
            <Card bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
              <CardBody>
                <Stat>
                  <StatLabel color={textColor}>
                    <HStack spacing={2}>
                      <FaCalendarAlt color="teal" />
                      <Text>کل ملاقات‌ها</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color={textColor}>{stats.totalAppointments}</StatNumber>
                  <StatHelpText>تعداد کل ملاقات‌های شما</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
              <CardBody>
                <Stat>
                  <StatLabel color={textColor}>
                    <HStack spacing={2}>
                      <FaClock color="blue" />
                      <Text>ملاقات‌های امروز</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color={textColor}>{stats.todayAppointments}</StatNumber>
                  <StatHelpText>ملاقات‌های برنامه‌ریزی شده امروز</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
              <CardBody>
                <Stat>
                  <StatLabel color={textColor}>
                    <HStack spacing={2}>
                      <FaUsers color="green" />
                      <Text>کل مراجعان</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color={textColor}>{stats.totalClients}</StatNumber>
                  <StatHelpText>تعداد کل مراجعان شما</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* آمار مالی و عملکرد */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={8}>
            <Card bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
              <CardBody>
                <Stat>
                  <StatLabel color={textColor}>
                    <HStack spacing={2}>
                      <FaMoneyBillWave color="green" />
                      <Text>درآمد امروز</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color={textColor}>{stats.todayEarnings?.toLocaleString() || 0}</StatNumber>
                  <StatHelpText>تومان</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
              <CardBody>
                <Stat>
                  <StatLabel color={textColor}>
                    <HStack spacing={2}>
                      <FaStar color="orange" />
                      <Text>نرخ تکمیل</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color={textColor}>{stats.completionRate?.toFixed(1) || 0}%</StatNumber>
                  <StatHelpText>ملاقات‌های تکمیل شده</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
              <CardBody>
                <Stat>
                  <StatLabel color={textColor}>
                    <HStack spacing={2}>
                      <FaUserClock color="teal" />
                      <Text>نرخ حضور</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color={textColor}>{stats.attendanceRate?.toFixed(1) || 0}%</StatNumber>
                  <StatHelpText>مراجعان حاضر</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* ملاقات‌های امروز که هنوز ساعتشان فرا نرسیده */}
          <Card bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" mb={8}>
            <CardHeader>
              <Heading size="md" color={textColor}>
                <HStack spacing={2}>
                  <FaClock color="blue" />
                  <Text>ملاقات‌های امروز شما (قبل از ساعت فعلی)</Text>
                </HStack>
              </Heading>
            </CardHeader>
            <CardBody>
              {stats.upcomingTodayAppointments.length > 0 ? (
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th color={textColor}>مراجع</Th>
                      <Th color={textColor}>ساعت</Th>
                      <Th color={textColor}>مدت</Th>
                      <Th color={textColor}>وضعیت</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {stats.upcomingTodayAppointments.map((appointment) => (
                      <Tr key={appointment._id}>
                        <Td color={textColor}>
                          {appointment.client?.firstName && appointment.client?.lastName
                            ? `${appointment.client.firstName} ${appointment.client.lastName}`
                            : appointment.client?.name || 'نامشخص'}
                        </Td>
                        <Td color={textColor}>{appointment.time}</Td>
                        <Td color={textColor}>{appointment.duration} دقیقه</Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(appointment.status)}>
                            {getStatusText(appointment.status)}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Text textAlign="center" color="gray.500" p={4}>
                  هیچ ملاقات‌ای برای امروز قبل از ساعت فعلی وجود ندارد
                </Text>
              )}
            </CardBody>
          </Card>

          {/* گزارش وضعیت‌های ملاقات */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
            <Card bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
              <CardHeader>
                <Heading size="md" color={textColor}>
                  <HStack spacing={2}>
                    <FaCheckCircle color="green" />
                    <Text>وضعیت ملاقات‌های شما (امروز)</Text>
                  </HStack>
                </Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={3} align="stretch">
                  {Object.entries(stats.todayStatusStats).map(([status, count]) => (
                    <Box key={status}>
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text color={textColor} fontSize="sm">
                          {getStatusText(status)}
                        </Text>
                        <Badge colorScheme={getStatusColor(status)}>
                          {count}
                        </Badge>
                      </Flex>
                      <Progress 
                        value={(count / stats.todayAppointments) * 100} 
                        colorScheme={getStatusColor(status)}
                        size="sm"
                        borderRadius="md"
                      />
                    </Box>
                  ))}
                </VStack>
              </CardBody>
            </Card>

            <Card bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
              <CardHeader>
                <Heading size="md" color={textColor}>
                  <HStack spacing={2}>
                    <FaMoneyBillWave color="green" />
                    <Text>وضعیت پرداخت‌های شما (امروز)</Text>
                  </HStack>
                </Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={3} align="stretch">
                  {Object.entries(stats.todayPaymentStats).map(([status, count]) => (
                    <Box key={status}>
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text color={textColor} fontSize="sm">
                          {getPaymentStatusText(status)}
                        </Text>
                        <Badge colorScheme={getPaymentStatusColor(status)}>
                          {count}
                        </Badge>
                      </Flex>
                      <Progress 
                        value={(count / stats.todayAppointments) * 100} 
                        colorScheme={getPaymentStatusColor(status)}
                        size="sm"
                        borderRadius="md"
                      />
                    </Box>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* گزارش کلی وضعیت‌ها */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            <Card bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
              <CardHeader>
                <Heading size="md" color={textColor}>
                  <HStack spacing={2}>
                    <FaCheckCircle color="blue" />
                    <Text>وضعیت کلی ملاقات‌های شما</Text>
                  </HStack>
                </Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={3} align="stretch">
                  {Object.entries(stats.appointmentStatusStats).map(([status, count]) => (
                    <Box key={status}>
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text color={textColor} fontSize="sm">
                          {getStatusText(status)}
                        </Text>
                        <Badge colorScheme={getStatusColor(status)}>
                          {count}
                        </Badge>
                      </Flex>
                      <Progress 
                        value={(count / stats.totalAppointments) * 100} 
                        colorScheme={getStatusColor(status)}
                        size="sm"
                        borderRadius="md"
                      />
                    </Box>
                  ))}
                </VStack>
              </CardBody>
            </Card>

            <Card bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">
              <CardHeader>
                <Heading size="md" color={textColor}>
                  <HStack spacing={2}>
                    <FaMoneyBillWave color="blue" />
                    <Text>وضعیت کلی پرداخت‌های شما</Text>
                  </HStack>
                </Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={3} align="stretch">
                  {Object.entries(stats.paymentStatusStats).map(([status, count]) => (
                    <Box key={status}>
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text color={textColor} fontSize="sm">
                          {getPaymentStatusText(status)}
                        </Text>
                        <Badge colorScheme={getPaymentStatusColor(status)}>
                          {count}
                        </Badge>
                      </Flex>
                      <Progress 
                        value={(count / stats.totalAppointments) * 100} 
                        colorScheme={getPaymentStatusColor(status)}
                        size="sm"
                        borderRadius="md"
                      />
                    </Box>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        </Box>
      </Box>
    </Flex>
  );
}

export default ConsultantDashboard; 